import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Volume2, VolumeX, Mic, Square, Settings2, X, Loader2, Sparkles, GripVertical, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useVoice, voiceSessionId } from "@/contexts/VoiceAssistantContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POS_KEY = "ca_voice_pos_v1";
const DOCK_KEY = "ca_voice_docked_v1";
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

/** Arrastre + anclaje al borde del panel flotante (persistente). */
const useDraggableDock = () => {
  const [pos, setPos] = useState(() => { try { return JSON.parse(localStorage.getItem(POS_KEY) || "null"); } catch { return null; } });
  const [docked, setDockedState] = useState(() => { try { return localStorage.getItem(DOCK_KEY) === "1"; } catch { return false; } });
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const setDocked = useCallback((d) => { setDockedState(d); try { localStorage.setItem(DOCK_KEY, d ? "1" : "0"); } catch { /* noop */ } }, []);
  const onPointerDown = useCallback((e) => {
    const el = e.currentTarget.parentElement?.parentElement;
    const r = el?.getBoundingClientRect();
    drag.current = { sx: e.clientX, sy: e.clientY, ox: r?.left || 0, oy: r?.top || 0, w: r?.width || 200, h: r?.height || 46 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
  }, []);
  const onPointerMove = useCallback((e) => {
    if (!drag.current) return;
    const d = drag.current;
    setPos({ x: clamp(d.ox + e.clientX - d.sx, 4, window.innerWidth - d.w - 4), y: clamp(d.oy + e.clientY - d.sy, 4, window.innerHeight - d.h - 4), w: d.w });
  }, []);
  const onPointerUp = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    setPos((p) => { if (p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* noop */ } } return p; });
  }, []);
  const side = pos ? (pos.x + 100 > window.innerWidth / 2 ? "right" : "left") : "left";
  return { pos, docked, setDocked, dragging, side, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
};

const PAGE_GUIDES = {
  "/": {
    visitor: "Bienvenido a CrisAngel Astral. Soy tu asistente de voz y te acompaño en todo el sitio. Aquí Cristopher ofrece lecturas de tarot, baraja española y runas, y trabajos espirituales con resultados reales. Si quieres ver precios y hablar con él, regístrate gratis con el botón de arriba. Si tienes una duda, toca el micrófono y pregúntame.",
    user: (n) => `Hola ${n}. Ya estás dentro. Desde el chat dorado, abajo a la derecha, puedes hablar con Cristopher, registrar tu pago y agendar tu cita sin salir del chat. Si necesitas algo, toca el micrófono y dímelo.`,
  },
  "/servicios": {
    visitor: "Estás viendo los servicios. Toca cualquiera para conocer los detalles. Los precios se muestran al registrarte, es gratis y toma un minuto.",
    user: (n) => `${n}, aquí están todos los servicios con sus precios. Elige el que necesitas y toca Me interesa: te mostraré los métodos de pago y podrás subir tu comprobante.`,
  },
  "/mi-cuenta": { user: (n) => `${n}, esta es tu cuenta. Arriba tienes pestañas: Perfil, Mensajes, Notificaciones, Citas y Pagos. En Pagos puedes subir tu comprobante y en Citas ver tus horarios.` },
  "/bienvenida": { visitor: "Aquí tienes los videos de bienvenida y los pasos para comenzar. Mírelos con calma y, cuando estés listo, regístrate para continuar.", user: () => "Aquí tienes los videos de bienvenida y los pasos para comenzar." },
  "/testimonios": { user: () => "Estos son testimonios reales de clientes de Cristopher." },
};

export const VoiceAssistantFloat = () => {
  const { user, token, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const v = useVoice();
  const [showOptIn, setShowOptIn] = useState(false);
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const bubbleTimer = useRef(null);
  const dock = useDraggableDock();
  useEffect(() => {
    const h = (e) => setChatOpen(e.detail === "open");
    window.addEventListener("ca:chatState", h);
    return () => window.removeEventListener("ca:chatState", h);
  }, []);

  const firstName = (user?.name || user?.username || "").split(" ")[0];
  const hidden = v.isAdmin || ["/adminpanel", "/admin", "/vidente-virtual"].some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (v.isAdmin || v.asked || hidden) return undefined;
    const t = setTimeout(() => setShowOptIn(true), 1200);
    return () => clearTimeout(t);
  }, [v.isAdmin, v.asked, hidden]);

  const showBubble = useCallback((text, ms = 9000) => {
    setBubble(text);
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setBubble(null), ms);
  }, []);

  // Guía de página (una vez por sesión y por ruta)
  useEffect(() => {
    if (!v.isOn || hidden || authLoading) return undefined;
    const key = Object.keys(PAGE_GUIDES).find((k) => (k === "/" ? pathname === "/" : pathname.startsWith(k)));
    if (!key) return undefined;
    const g = PAGE_GUIDES[key];
    const text = user ? (g.user ? g.user(firstName) : null) : g.visitor;
    if (!text) return undefined;
    const t = setTimeout(() => { if (!document.querySelector('[data-testid="tutorial-overlay"]')) v.speak(text, { id: `page:${key}:${user ? "u" : "v"}`, interrupt: false }); }, 900);
    return () => clearTimeout(t);
  }, [pathname, v.isOn, hidden, user, firstName, v, authLoading]);

  // Avisos en tiempo real: notificaciones, pagos aprobados/rechazados y respuestas del chat
  useRealtimeEvents({
    notification: (d) => { if (v.isOn && d?.title) v.speak(`Aviso: ${d.title}. ${d.body || ""}`, { interrupt: true, once: false }); },
    payment_request_decided: (d) => {
      if (!v.isOn) return;
      v.speak(d?.status === "approved"
        ? (d.fully_paid ? "Cristopher aprobó tu pago y tu servicio quedó liquidado. Ya puedes agendar tu cita desde el chat." : "Cristopher aprobó tu abono. Cuando hagas el siguiente pago, súbelo desde el chat.")
        : `Tu comprobante no fue aprobado. ${d?.motive || ""}. Puedes corregirlo y volver a enviarlo desde el chat.`, { once: false });
    },
    message: (m) => {
      if (!v.isOn || !m?.body || m.from_role !== "admin") return;
      v.speak(m.is_ai ? m.body : `Cristopher te escribió: ${m.body}`, { once: false, interrupt: true });
    },
  });

  const acceptVoice = (gender) => {
    v.setGender(gender);
    v.setEnabled(true);
    setShowOptIn(false);
    const key = pathname === "/" ? "/" : Object.keys(PAGE_GUIDES).find((k) => k !== "/" && pathname.startsWith(k));
    const g = key ? PAGE_GUIDES[key] : null;
    const text = user ? (g?.user ? g.user(firstName) : `Hola ${firstName}, voz activada. Te guiaré en cada paso.`) : (g?.visitor || "Voz activada. Te guiaré en cada paso.");
    setTimeout(() => v.speak(text, { id: `page:${key}:${user ? "u" : "v"}`, sticky: true, force: true }), 150);
  };

  const askByVoice = async () => {
    if (v.listening) { v.stopListening(); return; }
    const r = await v.listen();
    if (r.error) return toast.error(r.error);
    if (!r.text) return toast.message("No escuché nada, intenta de nuevo");
    showBubble(`Tú: ${r.text}`, 6000);
    setBusy(true);
    try {
      if (user && token) {
        await axios.post(`${API}/users/messages`, { body: r.text }, { headers: { Authorization: `Bearer ${token}` } });
        v.speak("Listo, envié tu mensaje a Cristopher. Te leo la respuesta en cuanto llegue.", { once: false });
        window.dispatchEvent(new CustomEvent("ca:openChat"));
      } else {
        const d = (await axios.post(`${API}/voice/ask`, { question: r.text, session_id: voiceSessionId(), page: pathname })).data;
        showBubble(d.answer, 14000);
        v.speak(d.answer, { once: false });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude procesar tu pregunta");
    } finally { setBusy(false); }
  };

  if (hidden) return null;

  return (
    <>
      {showOptIn && (
        <div data-testid="voice-optin-card" className="fixed inset-x-3 bottom-24 sm:inset-x-auto sm:left-4 sm:bottom-24 sm:w-80 z-[95] rounded-2xl border border-[#d4af37]/40 bg-[#0d0b14]/95 backdrop-blur-xl p-4 shadow-2xl animate-in slide-in-from-bottom-4">
          <button type="button" aria-label="Cerrar" onClick={() => { v.setEnabled(false); setShowOptIn(false); }} className="absolute top-2 right-2 text-neutral-500 hover:text-white"><X className="w-4 h-4" /></button>
          <div className="flex items-center gap-2 text-[#d4af37] font-semibold text-sm"><Volume2 className="w-4 h-4" /> ¿Quieres que te guíe con voz?</div>
          <p className="text-xs text-neutral-300 mt-1.5 leading-relaxed">Te leo cada paso en español claro: registro, pagos, cita, firma y chat. Puedes detenerme o apagarme cuando quieras.</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button type="button" data-testid="voice-optin-female" onClick={() => acceptVoice("female")} className="rounded-xl bg-[#d4af37] text-black text-xs font-semibold py-2 hover:bg-[#e6c35a]">Sí, voz femenina</button>
            <button type="button" data-testid="voice-optin-male" onClick={() => acceptVoice("male")} className="rounded-xl bg-[#d4af37]/20 border border-[#d4af37]/50 text-[#d4af37] text-xs font-semibold py-2 hover:bg-[#d4af37]/30">Sí, voz masculina</button>
          </div>
          <button type="button" data-testid="voice-optin-no" onClick={() => { v.setEnabled(false); setShowOptIn(false); }} className="w-full mt-2 text-[11px] text-neutral-400 hover:text-white py-1">No, gracias</button>
        </div>
      )}

      {v.asked && dock.docked && (
        <button type="button" data-testid="voice-dock-tab" onClick={() => dock.setDocked(false)} title="Mostrar asistente de voz"
          style={dock.pos ? { top: dock.pos.y } : undefined}
          className={`fixed z-[94] ${dock.side === "right" ? "right-0 rounded-l-xl" : "left-0 rounded-r-xl"} ${dock.pos ? "" : "top-1/2 -translate-y-1/2"} h-12 w-7 bg-[#0d0b14]/90 border border-[#d4af37]/40 backdrop-blur-xl flex items-center justify-center text-[#d4af37] shadow-xl`}>
          <Volume2 className={`w-3.5 h-3.5 ${v.speaking ? "animate-pulse" : ""}`} />
        </button>
      )}
      {v.asked && !dock.docked && (
        <div
          style={dock.pos ? (dock.side === "right"
            ? { right: Math.max(4, window.innerWidth - dock.pos.x - (dock.pos.w || 220)), left: "auto", top: dock.pos.y, bottom: "auto", transform: "none" }
            : { left: dock.pos.x, top: dock.pos.y, bottom: "auto", transform: "none" }) : undefined}
          className={`fixed left-2 z-[94] flex flex-col ${dock.side === "right" ? "items-end" : "items-start"} gap-2 ${dock.dragging ? "" : "transition-all"} ${dock.pos ? "" : chatOpen ? "top-16 sm:top-1/2 sm:-translate-y-1/2" : "bottom-16 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2"}`}
          data-testid="voice-float">
          {bubble && (
            <div data-testid="voice-bubble" className="max-w-[70vw] sm:max-w-xs rounded-2xl rounded-bl-sm border border-[#d4af37]/30 bg-[#0d0b14]/95 backdrop-blur-xl px-3 py-2 text-[12px] text-neutral-100 shadow-xl">{bubble}</div>
          )}
          <div className="flex items-center gap-1 rounded-full border border-[#d4af37]/40 bg-[#0d0b14]/90 backdrop-blur-xl p-1 shadow-xl select-none">
            <div data-testid="voice-drag-handle" {...dock.handlers} title="Arrastra para mover" className="w-5 h-9 flex items-center justify-center text-neutral-500 cursor-grab active:cursor-grabbing touch-none"><GripVertical className="w-4 h-4" /></div>
            <button type="button" data-testid="voice-toggle-btn" title={v.isOn ? "Voz activada (toca para apagar)" : "Voz apagada (toca para activar)"}
              onClick={() => v.setEnabled(!v.isOn)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition ${v.isOn ? "bg-[#d4af37] text-black" : "bg-white/10 text-neutral-400"}`}>
              {v.isOn ? <Volume2 className={`w-4 h-4 ${v.speaking ? "animate-pulse" : ""}`} /> : <VolumeX className="w-4 h-4" />}
            </button>
            {v.speaking && (
              <button type="button" data-testid="voice-stop-btn" onClick={v.stop} className="h-9 px-3 rounded-full bg-red-500/20 text-red-200 text-[11px] font-semibold flex items-center gap-1 hover:bg-red-500/30"><Square className="w-3 h-3" /> Detener</button>
            )}
            {v.isOn && (
              <button type="button" data-testid="voice-mic-btn" title="Pregúntame con tu voz" onClick={askByVoice} disabled={busy}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition ${v.listening ? "bg-red-500 text-white animate-pulse" : "bg-white/10 text-neutral-200 hover:bg-white/20"}`}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button type="button" data-testid="voice-settings-btn" onClick={() => setOpen(!open)} className="w-9 h-9 rounded-full bg-white/5 text-neutral-400 hover:text-white flex items-center justify-center"><Settings2 className="w-4 h-4" /></button>
            <button type="button" data-testid="voice-dock-btn" onClick={() => { setOpen(false); dock.setDocked(true); }} title="Ocultar en el borde" className="w-6 h-9 rounded-full text-neutral-500 hover:text-white flex items-center justify-center">
              {dock.side === "right" ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          {open && (
            <div data-testid="voice-settings" className="w-64 rounded-2xl border border-white/10 bg-[#0d0b14]/95 backdrop-blur-xl p-3 text-xs text-neutral-200 space-y-2 shadow-2xl">
              <div className="flex items-center justify-between"><span className="font-semibold text-[#d4af37] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Asistente de voz</span><button type="button" onClick={() => setOpen(false)}><X className="w-4 h-4 text-neutral-500" /></button></div>
              <label className="flex items-center justify-between">Voz activada<input data-testid="voice-enabled-switch" type="checkbox" checked={v.isOn} onChange={(e) => v.setEnabled(e.target.checked)} className="accent-[#d4af37] w-4 h-4" /></label>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Tipo de voz</div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button type="button" data-testid="voice-gender-female" onClick={() => v.setGender("female")} className={`rounded-lg py-1.5 ${v.gender === "female" ? "bg-[#d4af37] text-black" : "bg-white/5"}`}>Femenina</button>
                  <button type="button" data-testid="voice-gender-male" onClick={() => v.setGender("male")} className={`rounded-lg py-1.5 ${v.gender === "male" ? "bg-[#d4af37] text-black" : "bg-white/5"}`}>Masculina</button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Velocidad: {v.speed.toFixed(1)}x</div>
                <input data-testid="voice-speed" type="range" min="0.8" max="1.3" step="0.1" value={v.speed} onChange={(e) => v.setSpeed(Number(e.target.value))} className="w-full accent-[#d4af37]" />
              </div>
              <div className="flex gap-1.5">
                <button type="button" data-testid="voice-repeat" onClick={() => v.lastText && v.speak(v.lastText, { once: false })} className="flex-1 rounded-lg bg-white/5 py-1.5 hover:bg-white/10">Repetir</button>
                <button type="button" data-testid="voice-test" onClick={() => v.speak(`Hola${firstName ? ` ${firstName}` : ""}, así sueno. Estoy aquí para guiarte.`, { once: false })} className="flex-1 rounded-lg bg-white/5 py-1.5 hover:bg-white/10">Probar voz</button>
              </div>
              {!user && <button type="button" data-testid="voice-create-account" onClick={() => navigate("/registro")} className="w-full rounded-lg bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] py-1.5">Crear mi cuenta gratis</button>}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default VoiceAssistantFloat;
